/**
 * @file useNavigationHistory.ts
 * @module utils/useNavigationHistory
 * @description Provides navigation helpers that preserve route history and contextual state across UTrade Admin workflows.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { getKey, removeKey, setKey } from '../Global/history';

interface NavigationState {
    from?: string;
    [key: string]: any;
}

export const useNavigationHistory = () => {
    const navigate = useNavigate();
    const location = useLocation() as unknown as { state: NavigationState; pathname: string };

    const navigateTo = (path: string, state?: any) => {
        const sourcePagePath = location.pathname;
        setKey('lastPath', sourcePagePath, false);

        // Persist the navigate state for this destination path so that if the
        // breadcrumb "back" link points to this page later, it can restore state.
        if (state?.state !== undefined) {
            setKey('navState__' + path, state.state, true);
        }

        navigate(path, { ...state });
    };

    const goBackToSource = () => {
        let from = getKey('lastPath');
        
        if (from && typeof from === 'string') {
            try {
                from = JSON.parse(from);
            } catch (e) {
                // It's already a plain string, use as is
            }
        }
        
        if (from && typeof from === 'string') {
            navigate(from);
            removeKey('lastPath');
        } else {
            window.history.back();
        }
    };

    return {
        navigateTo,
        goBackToSource,
        sourcePagePath: (() => {
            let path = getKey('lastPath');
            if (path && typeof path === 'string') {
                try {
                    path = JSON.parse(path);
                } catch (e) {
                    // Already a plain string
                }
            }
            return path;
        })(),
    };
};
